import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Checkbox,
  DatePicker,
  Flex,
  OverlayStyle,
  SlideoutDrawerV2,
  Text,
  TextInput,
} from "@jsq/ds/core";
import { client } from "~/apollo/client";
import { TransactionDetailViewerDocument } from "~/graphql/supergraphHooks";
import { toFederationGlobalId } from "~/utils/graphql-helpers";
import { Transaction, TransactionDetailRow } from "../../types";
import { formatCurrency, formatDate, formatTransactionStatus } from "../../utils";
import type { TransactionDetailViewerQuery } from "~/graphql/supergraphTypes";

interface TransactionDetailDrawerProps {
  transaction: Transaction | null;
  onClose: () => void;
}

interface DetailRowProps {
  label: string;
  value: string | number | null | undefined;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => (
  <Flex
    justifyContent="space-between"
    style={{ padding: "8px 0", borderBottom: "1px solid #eee" }}
  >
    <Text color="secondary" style={{ fontSize: "14px" }}>
      {label}
    </Text>
    <Text style={{ fontSize: "14px", fontWeight: 500 }}>{value ?? "--"}</Text>
  </Flex>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, children }) => (
  <Flex flexDirection="column" style={{ marginBottom: "24px" }}>
    <Text style={{ fontSize: "16px", fontWeight: 600, marginBottom: "12px" }}>{title}</Text>
    {children}
  </Flex>
);

const formatDateForFilter = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseFilterDate = (value?: string): Date | null => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const parsePositionIds = (raw: string): number[] | undefined => {
  const ids = raw
    .split(/[,\s]+/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part) => parseInt(part, 10))
    .filter((n) => !Number.isNaN(n));
  return ids.length > 0 ? ids : undefined;
};

export const TransactionDetailDrawer: React.FC<TransactionDetailDrawerProps> = ({
  transaction,
  onClose,
}) => {
  const [effectiveDate, setEffectiveDate] = useState<string | undefined>();
  const [targetPattern, setTargetPattern] = useState("");
  const [jsqPositionIdsText, setJsqPositionIdsText] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [detailRows, setDetailRows] = useState<TransactionDetailRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadRequestIdRef = useRef(0);

  const loadDetails = useCallback(
    async (overrides?: {
      effectiveDate?: string;
      targetPattern?: string;
      jsqPositionIdsText?: string;
      includeDeleted?: boolean;
    }) => {
      if (!transaction) return;

      const nextEffectiveDate =
        overrides && "effectiveDate" in overrides ? overrides.effectiveDate : effectiveDate;
      const nextTargetPattern =
        overrides && "targetPattern" in overrides
          ? (overrides.targetPattern ?? "")
          : targetPattern;
      const nextPositionText =
        overrides && "jsqPositionIdsText" in overrides
          ? (overrides.jsqPositionIdsText ?? "")
          : jsqPositionIdsText;
      const nextIncludeDeleted =
        overrides && "includeDeleted" in overrides
          ? (overrides.includeDeleted ?? false)
          : includeDeleted;

      const requestId = ++loadRequestIdRef.current;
      setIsLoading(true);
      setError(null);

      try {
        const response = await client.query<TransactionDetailViewerQuery>({
          query: TransactionDetailViewerDocument,
          variables: {
            arenaId: toFederationGlobalId("ArenaObjectType", transaction.arenaId),
            effectiveDate: nextEffectiveDate ?? null,
            targetPattern: nextTargetPattern.trim() || null,
            jsqPositionIds: parsePositionIds(nextPositionText) ?? null,
            includeDeleted: nextIncludeDeleted,
            offset: 0,
            limit: 50,
          },
          fetchPolicy: "no-cache",
        });

        if (requestId !== loadRequestIdRef.current) return;

        const result = response.data.transactionDetailViewer;
        setDetailRows(result.transactions || []);
        setTotalCount(result.totalCount);
      } catch (err: unknown) {
        if (requestId !== loadRequestIdRef.current) return;

        const message =
          (err as { graphQLErrors?: { message?: string }[]; message?: string })
            ?.graphQLErrors?.[0]?.message ??
          (err as { message?: string })?.message ??
          "Failed to load investigation details.";
        setError(message);
        setDetailRows([]);
        setTotalCount(0);
      } finally {
        if (requestId === loadRequestIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [transaction, effectiveDate, targetPattern, jsqPositionIdsText, includeDeleted],
  );

  const loadDetailsRef = useRef(loadDetails);
  loadDetailsRef.current = loadDetails;

  useEffect(() => {
    if (!transaction) return;
    const nextEffective = transaction.effectiveDate || undefined;
    const nextPositions = transaction.jsqPositionIds?.join(", ") ?? "";
    setEffectiveDate(nextEffective);
    setTargetPattern("");
    setJsqPositionIdsText(nextPositions);
    setIncludeDeleted(false);
    setDetailRows([]);
    setTotalCount(0);
    setError(null);
    void loadDetailsRef.current({
      effectiveDate: nextEffective,
      targetPattern: "",
      jsqPositionIdsText: nextPositions,
      includeDeleted: false,
    });
  }, [transaction?.glTransactionId]);

  const labelStyle: React.CSSProperties = useMemo(
    () => ({
      fontSize: "13px",
      fontWeight: 500,
      color: "#495057",
      marginBottom: "4px",
    }),
    [],
  );

  if (!transaction) {
    return null;
  }

  return (
    <SlideoutDrawerV2
      isOpen={!!transaction}
      onClose={onClose}
      isDismissable
      overlayStyle={OverlayStyle.Transparent}
      data-testid="transaction-detail-drawer"
    >
      <Flex
        flexDirection="column"
        style={{
          width: "560px",
          height: "100%",
          overflow: "hidden",
          backgroundColor: "#ffffff",
          boxShadow: "-4px 0 20px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Flex
          justifyContent="space-between"
          alignItems="center"
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #eee",
            flexShrink: 0,
          }}
        >
          <Text style={{ fontSize: "18px", fontWeight: 600 }}>Transaction Details</Text>
          <Button variant="neutral" onClick={onClose} aria-label="Close drawer" icon="times" />
        </Flex>

        <Flex
          flexDirection="column"
          style={{
            padding: "24px",
            overflowY: "auto",
            flex: 1,
          }}
        >
          <Section title="Transaction Information">
            <DetailRow label="GL Transaction ID" value={transaction.glTransactionId} />
            <DetailRow label="Arena ID" value={transaction.arenaId} />
            <DetailRow label="Entity ID" value={transaction.entityId} />
            <DetailRow label="Position ID" value={transaction.positionId} />
            <DetailRow label="Effective Date" value={transaction.effectiveDate} />
            <DetailRow label="GL Date" value={transaction.glDate} />
            <DetailRow
              label="Status"
              value={formatTransactionStatus(transaction.transactionStatus)}
            />
            <DetailRow label="GL Transaction Type" value={transaction.transactionType} />
          </Section>

          <Section title="Financial Details">
            <DetailRow
              label="Total Amount"
              value={formatCurrency(transaction.totalAmount, transaction.transactionCurrency)}
            />
            <DetailRow
              label="Converted Credit Amount"
              value={formatCurrency(
                transaction.totalConvertedCreditAmount,
                transaction.transactionCurrency,
              )}
            />
            <DetailRow label="Currency" value={transaction.transactionCurrency} />
            <DetailRow label="Debit/Credit" value={transaction.debitOrCredit} />
          </Section>

          <Section title="Investigation filters">
            <Flex flexDirection="column" style={{ gap: "12px", marginBottom: "12px" }}>
              <Flex flexDirection="column">
                <label htmlFor="detail-effective-date" style={labelStyle}>
                  Effective date
                </label>
                <DatePicker
                  id="detail-effective-date"
                  selected={parseFilterDate(effectiveDate)}
                  onChange={(date: Date | null) =>
                    setEffectiveDate(date ? formatDateForFilter(date) : undefined)
                  }
                  dateFormat="MM/dd/yyyy"
                  hasIcon
                  placeholderText="Select date"
                  data-testid="detail-effective-date"
                />
              </Flex>
              <Flex flexDirection="column">
                <label htmlFor="detail-target-pattern" style={labelStyle}>
                  Target pattern (prefix match)
                </label>
                <TextInput
                  id="detail-target-pattern"
                  placeholder="e.g. net_income"
                  value={targetPattern}
                  onChange={(e) => setTargetPattern(e.target.value)}
                  data-testid="detail-target-pattern"
                />
              </Flex>
              <Flex flexDirection="column">
                <label htmlFor="detail-jsq-position-ids" style={labelStyle}>
                  JSQ position IDs
                </label>
                <TextInput
                  id="detail-jsq-position-ids"
                  placeholder="Comma-separated IDs"
                  value={jsqPositionIdsText}
                  onChange={(e) => setJsqPositionIdsText(e.target.value)}
                  data-testid="detail-jsq-position-ids"
                />
              </Flex>
              <Checkbox
                name="detail-include-deleted"
                label="Include deleted"
                checked={includeDeleted}
                onChange={() => setIncludeDeleted((v) => !v)}
                data-testid="detail-include-deleted"
              />
              <Button
                variant="neutral"
                onClick={() => {
                  void loadDetails();
                }}
                isLoading={isLoading}
                data-testid="detail-apply-filters"
              >
                Apply filters
              </Button>
            </Flex>
            {error && (
              <Text style={{ color: "#dc3545", fontSize: "13px", marginBottom: "8px" }}>
                {error}
              </Text>
            )}
            <Text color="secondary" style={{ fontSize: "13px", marginBottom: "8px" }}>
              {isLoading
                ? "Loading mapping history…"
                : `Showing ${detailRows.length} of ${totalCount} mapping-history rows`}
            </Text>
            {detailRows.length === 0 && !isLoading && !error && (
              <Text color="secondary" style={{ fontSize: "13px" }}>
                No mapping-history rows for these filters.
              </Text>
            )}
            {detailRows.map((row) => (
              <Flex
                key={`${row.transactionId}-${row.historyCreatedAt}-${row.target}`}
                flexDirection="column"
                style={{
                  padding: "12px",
                  marginBottom: "8px",
                  border: "1px solid #e9ecef",
                  borderRadius: "6px",
                  gap: "4px",
                }}
                data-testid="detail-history-row"
              >
                <DetailRow label="Txn ID" value={row.transactionId} />
                <DetailRow label="JSQ Position ID" value={row.jsqPositionId} />
                <DetailRow label="Target" value={row.target} />
                <DetailRow label="Memo" value={row.memo} />
                <DetailRow
                  label="History amount"
                  value={formatCurrency(row.historyAmount, transaction.transactionCurrency)}
                />
                <DetailRow label="History effective date" value={row.historyEffectiveDate} />
                <DetailRow
                  label="Status"
                  value={formatTransactionStatus(row.transactionStatus)}
                />
                <DetailRow
                  label="Processed"
                  value={row.transactionIsProcessed ? "Yes" : "No"}
                />
                <DetailRow label="Batch source ID" value={row.glBatchSourceObjectId} />
                <DetailRow label="Batch category" value={row.glBatchCategory} />
                <DetailRow label="History created" value={formatDate(row.historyCreatedAt)} />
              </Flex>
            ))}
          </Section>

          <Section title="Aggregated Investor Details">
            <DetailRow label="Transaction Count" value={transaction.transactionCount} />
            <DetailRow
              label="Total Shares"
              value={
                transaction.totalShares != null
                  ? new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 6,
                    }).format(parseFloat(transaction.totalShares))
                  : null
              }
            />
            <DetailRow
              label="Total Quantity"
              value={
                transaction.totalQuantity != null
                  ? new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 6,
                    }).format(parseFloat(transaction.totalQuantity))
                  : null
              }
            />
            <DetailRow
              label="Avg Cost/Share"
              value={
                transaction.avgCostPerShare != null
                  ? formatCurrency(transaction.avgCostPerShare, transaction.transactionCurrency)
                  : null
              }
            />
            <DetailRow
              label="JSQ Position IDs"
              value={transaction.jsqPositionIds?.join(", ") || null}
            />
          </Section>

          <Section title="Account Information">
            <DetailRow label="GL Account Number" value={transaction.glAccountNumber} />
            <DetailRow label="GL Account Type" value={transaction.glAccountType} />
            <DetailRow label="Bank Account" value={transaction.bankAccount} />
          </Section>

          <Section title="Invoice Details">
            <DetailRow label="Invoice Number" value={transaction.invoiceNumber} />
            <DetailRow label="Invoice Due Date" value={transaction.invoiceDueDate} />
            <DetailRow label="Vendor" value={transaction.vendor} />
          </Section>

          <Section title="Batch Information">
            <DetailRow label="Batch Type" value={transaction.batchType} />
            <DetailRow label="Batch Category" value={transaction.batchCategory} />
            <DetailRow label="Batch Reference" value={transaction.batchReference} />
            <DetailRow label="Batch Comments" value={transaction.batchComments} />
            <DetailRow label="Batch Source Object ID" value={transaction.batchSourceObjectId} />
            <DetailRow
              label="Batch Created At"
              value={formatDate(transaction.batchCreatedAt)}
            />
          </Section>

          <Section title="Related Information">
            <DetailRow label="Related Party" value={transaction.relatedParty} />
            <DetailRow label="Deal Name" value={transaction.dealName} />
            <DetailRow
              label="Capital Event Reference"
              value={transaction.capitalEventReference}
            />
            <DetailRow label="Sources/Uses" value={transaction.sourcesUses} />
            <DetailRow label="Description" value={transaction.transactionDescription} />
          </Section>

          <Section title="Timestamps">
            <DetailRow label="Created At" value={formatDate(transaction.createdAt)} />
            <DetailRow label="Modified At" value={formatDate(transaction.modifiedAt)} />
            <DetailRow label="Deleted At" value={formatDate(transaction.deletedAt)} />
          </Section>
        </Flex>
      </Flex>
    </SlideoutDrawerV2>
  );
};
